import { ApolloError, useMutation } from '@apollo/client';

import { deletePredictiveAudienceMutation } from 'api/mutations/predictiveAudiences';
import noop from 'utils/noop';
import { RUMHelpers } from 'services/observability/datadog';
import { useCoreGoalsContext } from 'modules/predictiveGoals/components/contexts/CoreGoalsContext/CoreGoalsContext';
import { createActionName, reportGoalDeleteError } from '../../observabilityUtils';

import { PredictiveAudienceGoal, DeletePredictiveGoalResponse } from '../../types';

export type onDeletionError = {
  (error: ApolloError): void;
};
export type onDeletionQuery<QueryData> = {
  (data: QueryData): Promise<any> | void;
};

export interface GoalDeleteHook<QueryData> {
  (
    refetchQuery?: onDeletionQuery<QueryData>,
    onDeleteError?: onDeletionError,
  ): [(id: number) => void, boolean];
}

export const useGoalDeleteMutation: GoalDeleteHook<PredictiveAudienceGoal> = (
  refetchQuery?: onDeletionQuery<PredictiveAudienceGoal>,
  onDeleteError?: onDeletionError,
) => {
  const maybeRefetchQuery = refetchQuery || noop;
  const maybeErrorHandler = onDeleteError || noop;
  const {
    goals: { retrigger },
  } = useCoreGoalsContext();
  const [deleteGoal, { loading: isDeleting }] = useMutation<DeletePredictiveGoalResponse>(
    deletePredictiveAudienceMutation,
    {
      onCompleted({ predictiveAudienceGoalDelete }) {
        retrigger();
        RUMHelpers.addRUMAction(createActionName('Goal deletion, refetching'));
        maybeRefetchQuery(predictiveAudienceGoalDelete);
      },
      onError(error: ApolloError) {
        reportGoalDeleteError(error);
        maybeErrorHandler(error);
      },
    },
  );
  const deleteGoalById = (id: number) => {
    deleteGoal({ variables: { id } });
  };
  return [deleteGoalById, isDeleting];
};
